import React from 'react';
import { calculateFormatOutput } from './scenarioCalculations';
import Drawer from '../common/Drawer';
import './CreateProductionPlanModal.css';

const CreateProductionPlanModal = ({
    displayedProducts,
    formatAllocations,
    modal,
    targetAllocations,
    viewMode,
    getAllocatedKegsForProduct,
    getAvailableKegsForProduct,
    onClose,
    onCreatePlan,
    onPlanEndDateChange,
    onPlanNameChange,
    onPlanNotesChange,
    onPlanStartDateChange
}) => {
    // Escape, the overlay and the close button all route through this, so
    // the drawer can't be dismissed mid-submit.
    const handleClose = () => {
        if (!modal.isSubmitting) onClose();
    };

    const footer = (
        <>
            <button
                className="app-drawer-btn app-drawer-btn--secondary"
                onClick={handleClose}
                disabled={modal.isSubmitting}
            >
                Cancel
            </button>
            <button
                className="app-drawer-btn app-drawer-btn--primary gsp-footer-create-btn"
                onClick={onCreatePlan}
                disabled={modal.isSubmitting}
            >
                {modal.isSubmitting ? (
                    <>
                        <span className="spinner"></span>
                        Creating Plan...
                    </>
                ) : 'Create Production Plan'}
            </button>
        </>
    );

    return (
        <Drawer
            isOpen
            onClose={handleClose}
            title="Create Production Plan"
            subtitle="Review the scenario and configure your production plan before saving."
            footer={footer}
        >
                <div className="gsp-panel-content">
                    {/* Plan Information Group */}
                    <div className="gsp-panel-section">
                        <h3 className="gsp-section-title">Plan Information</h3>
                        
                        <div className="gsp-form-group">
                            <label className="gsp-form-label">Plan Name *</label>
                            <input
                                type="text"
                                value={modal.planName}
                                onChange={(event) => onPlanNameChange(event.target.value)}
                                placeholder="e.g., December Production Plan"
                                className="gsp-form-input"
                                disabled={modal.isSubmitting}
                            />
                        </div>

                        <div className="gsp-form-row">
                            <div className="gsp-form-group">
                                <label className="gsp-form-label">Start Date *</label>
                                <input
                                    type="date"
                                    value={modal.planStartDate}
                                    onChange={(event) => onPlanStartDateChange(event.target.value)}
                                    className="gsp-form-input"
                                    disabled={modal.isSubmitting}
                                />
                            </div>
                            <div className="gsp-form-group">
                                <label className="gsp-form-label">End Date *</label>
                                <input
                                    type="date"
                                    value={modal.planEndDate}
                                    onChange={(event) => onPlanEndDateChange(event.target.value)}
                                    className="gsp-form-input"
                                    disabled={modal.isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="gsp-form-group">
                            <label className="gsp-form-label">Notes</label>
                            <textarea
                                value={modal.planNotes}
                                onChange={(event) => onPlanNotesChange(event.target.value)}
                                placeholder="Add any notes about this production plan..."
                                className="gsp-form-textarea"
                                rows="3"
                                disabled={modal.isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Products Included Group */}
                    <div className="gsp-panel-section">
                        <h3 className="gsp-section-title">Products Included</h3>
                        
                        <div className="gsp-products-preview-list">
                            {displayedProducts.map(product => {
                                const allocations = viewMode === 'target'
                                    ? targetAllocations[product.product_id] || {}
                                    : formatAllocations[product.product_id] || {};
                                const hasAllocations = Object.keys(allocations).length > 0;
                                const availableKegs = getAvailableKegsForProduct(product);
                                const allocatedKegs = viewMode === 'target'
                                    ? getTargetKegsNeededForProduct(allocations)
                                    : getAllocatedKegsForProduct(product.product_id);
                                const isOverAllocated = allocatedKegs > availableKegs;

                                return (
                                    <div 
                                        key={product.product_id} 
                                        className={`gsp-preview-product-card ${isOverAllocated ? 'gsp-card-overallocated' : ''}`}
                                    >
                                        <div className="gsp-card-product-header">
                                            <span className="gsp-card-product-name">{product.product_name}</span>
                                            {isOverAllocated && (
                                                <span className="gsp-card-warning-badge">⚠️ Over-Allocated</span>
                                            )}
                                        </div>

                                        <div className="gsp-card-product-allocations">
                                            {hasAllocations ? (
                                                Object.entries(allocations).map(([formatId, kegs]) => {
                                                    if (kegs <= 0) return null;

                                                    if (viewMode === 'target') {
                                                        const format = calculateFormatOutput(0, formatId).format;
                                                        const concentrateKegs = kegs / format.per_concentrate_keg;

                                                        return (
                                                            <div key={formatId} className="gsp-card-allocation-row">
                                                                <span className="gsp-card-allocation-format">{format.unit}</span>
                                                                <span className="gsp-card-allocation-qty">
                                                                    {Math.floor(kegs).toLocaleString()} units ({concentrateKegs.toFixed(1)} kegs)
                                                                </span>
                                                            </div>
                                                        );
                                                    }

                                                    const { units, format } = calculateFormatOutput(kegs, formatId);

                                                    return (
                                                        <div key={formatId} className="gsp-card-allocation-row">
                                                            <span className="gsp-card-allocation-format">{format.unit}</span>
                                                            <span className="gsp-card-allocation-qty">
                                                                {Math.floor(units).toLocaleString()} units ({kegs.toFixed(1)} kegs)
                                                            </span>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="gsp-card-no-allocation">No Allocation</div>
                                            )}
                                        </div>

                                        {isOverAllocated && (
                                            <div className="gsp-card-allocation-error">
                                                Allocated: {allocatedKegs.toFixed(1)} / {availableKegs.toFixed(1)} kegs
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {modal.submitMessage.text && (
                        <div className={`gsp-submit-message-card ${modal.submitMessage.type}`}>
                            {modal.submitMessage.text}
                        </div>
                    )}
                </div>
        </Drawer>
    );
};

const getTargetKegsNeededForProduct = (allocations) => {
    return Object.entries(allocations).reduce((sum, [formatId, targetUnits]) => {
        const format = calculateFormatOutput(0, formatId).format;
        return sum + (targetUnits / format.per_concentrate_keg);
    }, 0);
};

export default CreateProductionPlanModal;
